import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "table w-full columns" }
const _hoisted_2 = {
  key: 0,
  class: "line-h"
}
const _hoisted_3 = { class: "pad-m mleft-04" }
const _hoisted_4 = { class: "pad-m" }
const _hoisted_5 = { class: "row-top gap-02" }
const _hoisted_6 = { class: "col" }
const _hoisted_7 = { style: {"margin-left":"-0.1rem"} }
const _hoisted_8 = { class: "pad-m" }

import { computed, onBeforeUnmount } from 'vue'
import { useUiStore } from '@/stores/ui'
import { useCurrentEntryStore } from '@/stores/currentEntry'
import { getConceptExpressions, getTextBasedOnLanguage, findById } from '@/functions/expression'
import gen from "@/functions/general.js"
import {apiDateString} from "@/api/apicommon"
import { useAppUserStore } from '@/stores/appUser';
import { ConclusionGroup, FolderPatientApi, PatientCommonExpression } from '@/apiGenerate/medserver'
import { medServerApiConfig } from '@/config/medserver-api.config'
import { getSingleElement } from '@/utils/array-utils'
import { useRoute } from 'vue-router'
import { computedAsync } from '@vueuse/core'

interface DashbExprCclTable {
  entryStart : string,
  conclusionGroup: ConclusionGroup,
  expressions: Array<PatientCommonExpression>
}


export default /*@__PURE__*/_defineComponent({
  __name: 'dashbExprCcl',
  setup(__props) {

const appUserStore = useAppUserStore();
const currentEntryStore = useCurrentEntryStore();
const uiStore = useUiStore();
const route = useRoute();

const folderPatientApi = new FolderPatientApi(medServerApiConfig);

const shortTableList = computed(() => {
  const maxRows = 5
  return fullTableList.value?.slice(0, maxRows)
})

function createTableRow(entry, ccl) {
  const sctLanguage = appUserStore.currentAppUserData?.preferences.sctLanguage;

  //=Helper function for fullTableList
  const allExpressions = currentEntryStore.allExpressions;
  const entryDate = entry.entryStart
  const entryText = apiDateString(entryDate)
  const entryInMs = new Date(entryDate).getTime()
  //Add date and ccl
  let newRow = {
    "dateTime": {
      text: entryText,
      inMs: entryInMs
    },
    "ccl": gen.deepCopy(ccl),
    "results": []
  }
  //Add assessedExpr if ccl has it
  if (ccl.assessmentExpressionId) {
    newRow.assessedExpr = gen.deepCopy(findById(allExpressions, "expressionId", ccl.assessmentExpressionId));
    if (newRow.assessedExpr) {
     newRow.assessedExprText = getTextBasedOnLanguage(newRow.assessedExpr, sctLanguage)
    }
  }
  return newRow
}

function patientCommonExpressionToDashbExprCclTable(allExpressions : PatientCommonExpression[]) : DashbExprCclTable[] {
  return Object.values(
      allExpressions.reduce<Record<string, DashbExprCclTable>>((table, expr) => {
        const date = expr.entry.entryStart;
        if (!table[date]) {
          table[date] = {
            entryStart: date,
            conclusionGroup: expr.conclusionGroup,
            expressions: []
          };
        }
        table[date].expressions.push(expr);
        return table;
      }, {})
  );
}

const fullTableList = computedAsync(async () => {
  if (!currentEntryStore.hasCurrentEntry || !currentEntryStore.allExpressions) {return [];}

  const allExpressions = currentEntryStore.allExpressions
  const dashboardExpr = uiStore.dashBoardItem

  let tableArray : any[] = []
  //SAP
  if (dashboardExpr.expressionType === "SubstanceAdministrationPrescription") {
    //Get doseTimingIds for table
    const entries: DashbExprCclTable[] = patientCommonExpressionToDashbExprCclTable(allExpressions);

    if (entries?.length > 0) {
      entries.forEach(entry => {
        //Create new TableRow
        let newRow = createTableRow(entry, entry.conclusionGroup)
        //Create table data
        entry.expressions.forEach(expr => {
          expr.resultText = expr.matchedText + " " + expr.multiDosageText
          expr.isStopped = Boolean(expr.durationType === "Until")
          newRow.results.push(expr)
          //Add row to table
          tableArray.push(newRow)
          // }
        })
      });
    }

  //Non SAP
  } else {
    //Get expressionIds for table
    const allConceptExpressions = getConceptExpressions(dashboardExpr, allExpressions)
    let allMatchingProxies = allExpressions.filter(expr => {return (
      expr.isMatched && expr.sctProxyKey === dashboardExpr.sctProxyKey
    )})
    if (["Left", "Right"].includes(dashboardExpr.laterality)) {
      if (dashboardExpr.laterality === "Left") {
        allMatchingProxies = allMatchingProxies.filter(expr => expr.laterality !== "Right")
      } else if (dashboardExpr.laterality === "Right") {
        allMatchingProxies = allMatchingProxies.filter(expr => expr.laterality !== "Left")
      }
    }
    const allTableExpressionsIds = [...allConceptExpressions, ...allMatchingProxies].map(expr => expr.expressionId)
    const entries: DashbExprCclTable[] = patientCommonExpressionToDashbExprCclTable(allExpressions);

    //Create table data
    entries.forEach(entry => {

      // Find the correct conclusion group if the expression is an assessment expression
      const cg = entry.expressions.filter(expr => {return (
        expr.conclusionGroup && expr.conclusionGroup.assessmentExpressionId === dashboardExpr.expressionId
      )})

      //Create new TableRow
      let newRow = createTableRow(entry, cg.length >= 1 ? cg[0].conclusionGroup : entry.conclusionGroup)
      entry.expressions.forEach(expr => {
        const resultsForTable = [expr];
        const hasResultsInCcl = expr.originalText !== "" && allTableExpressionsIds.includes(expr.expressionId);
        const isAssessedExprInCcl = allTableExpressionsIds.includes(expr.conclusionGroup.assessmentExpressionId)
        const hasCclText = (expr.conclusionGroup.conclusionText !== "")
        if (hasResultsInCcl || (isAssessedExprInCcl && hasCclText)) {
          //Add matching results
          if (hasResultsInCcl) {
            resultsForTable.forEach(resultExpression => {
              resultExpression.resultText = resultExpression.originalText
              newRow.results.push(resultExpression)
            })
          }
        }
      })
      //Add row to table
      tableArray.push(newRow)
    });
  }
  //Sort
  tableArray.sort((a, b) => {return b.dateTime.inMs - a.dateTime.inMs || a.groupOrder - b.groupOrder})

  //UI-Data
  let uiIndex = 1
  tableArray.forEach(row => {
    row.rowIndex = ++uiIndex
  })
  return tableArray
})

onBeforeUnmount(() => {
  currentEntryStore.allExpressions = [];
});


return (_ctx: any,_cache: any) => {
  const _component_text_item = _resolveComponent("text-item")!
  const _component_svg_item = _resolveComponent("svg-item")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      style: _normalizeStyle([{'grid-row' : 2}, {"grid-column":"1 / -1"}])
    }, [
      (shortTableList.value?.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2))
        : _createCommentVNode("", true)
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shortTableList.value, (item1, index) => {
      return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
        _createElementVNode("div", {
          style: _normalizeStyle([{'grid-row' : item1.rowIndex + 1}, {"grid-column":"1 / -1"}])
        }, _cache[0] || (_cache[0] = [
          _createElementVNode("div", { class: "line-h" }, null, -1)
        ]), 4),
        _createElementVNode("div", {
          style: _normalizeStyle({'grid-area' : item1.rowIndex + ' / date'})
        }, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_text_item, {
              txt: item1.dateTime.text,
              styles: "main-h"
            }, null, 8, ["txt"])
          ])
        ], 4),
        _createElementVNode("div", {
          style: _normalizeStyle({'grid-area' : item1.rowIndex + ' / result'})
        }, [
          _createElementVNode("div", _hoisted_4, [
            (item1.assessedExpr)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_component_svg_item, {
                      icon: "text",
                      styles: "main-l size-m",
                      class: "mtop-01"
                    }),
                    _createVNode(_component_text_item, {
                      txt: item1.assessedExprText,
                      styles: "main-l",
                      class: "linebreak"
                    }, null, 8, ["txt"])
                  ]),
                  _cache[1] || (_cache[1] = _createElementVNode("div", { class: "mtop-02" }, null, -1))
                ], 64))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item1.results, (item2, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "row-top"
                }, [
                  (item2.isStopped)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createElementVNode("div", _hoisted_7, [
                          _createVNode(_component_svg_item, {
                            icon: "stop",
                            styles: "main-m size-m",
                            class: "mtop-01"
                          })
                        ]),
                        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "mleft-02" }, null, -1))
                      ], 64))
                    : _createCommentVNode("", true),
                  _createVNode(_component_text_item, {
                    txt: item2.resultText,
                    styles: "main-h",
                    class: "linebreak"
                  }, null, 8, ["txt"])
                ]))
              }), 128))
            ])
          ])
        ], 4),
        _createElementVNode("div", {
          style: _normalizeStyle({'grid-area' : item1.rowIndex + ' / ccl'})
        }, [
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_component_text_item, {
              txt: item1.ccl?.conclusionText || '',
              styles: "main-h",
              class: "linebreak"
            }, null, 8, ["txt"])
          ])
        ], 4)
      ], 64))
    }), 128))
  ]))
}
}

})